import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Popover from "antd/es/popover";
import cn from "classnames";
import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import Trend from "@app/component/Trend";
import dragStyles from "@app/scene/dashboard/components/styles/common.module.scss";
import Scoped from "@app/scene/dashboard/types/Scoped";
import { getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import { DefaultDateRangeConfiguration } from "@app/types/dashboard/CardConfiguration";
import ClosedStageDealAmountCardConfiguration from "@app/types/dashboard/cards/closedStageDealAmount/ClosedStageDealAmountCardConfiguration";
import { getDealStageTypeDisplayName } from "@app/util/ui";

import closedStageDealAmountCard from "..";
import messages from "../messages";

import styles from "./Title.module.scss";

interface Props extends Scoped {
  customTitle?: string;
  dateRange: DefaultDateRangeConfiguration;
  dealStageIds: Stage["id"][];
  editable?: boolean;
  onChangeConfiguration?: (configuration: Partial<ClosedStageDealAmountCardConfiguration>) => void;
  onTrendHover?: (hover: boolean) => void;
  stages: Stage[];
  trend?: number;
}

const Title: React.FC<Props> = ({
  customTitle,
  dateRange,
  dealStageIds,
  editable,
  onChangeConfiguration,
  onTrendHover,
  scope,
  stages,
  trend,
}) => {
  const intl = useIntl();
  const { DateRangeFormComponent } = closedStageDealAmountCard;
  const handleValuesChange = useCallback(
    (value) => onChangeConfiguration?.(value),
    [onChangeConfiguration]
  );

  const handleTrendMouseEnter = useCallback(() => onTrendHover?.(true), [onTrendHover]);
  const handleTrendMouseLeave = useCallback(() => onTrendHover?.(false), [onTrendHover]);

  const stageTypes = useMemo(() => {
    const stageTypes = [];
    const dealClosedStages = stages.filter(({ id }) => dealStageIds.includes(id));

    const hasClosedWon = dealClosedStages.some(({ type }) => type === DealStageType.WON);
    if (hasClosedWon) {
      stageTypes.push(DealStageType.WON);
    }

    const hasClosedLost = dealClosedStages.some(({ type }) => type === DealStageType.LOST);
    if (hasClosedLost) {
      stageTypes.push(DealStageType.LOST);
    }
    return stageTypes;
  }, [dealStageIds, stages]);

  return (
    <>
      {customTitle?.trim() ||
        intl.formatMessage(messages.cardTitle, {
          date: (text: string[]) =>
            editable ? (
              <Popover
                content={
                  <div className={styles.popover}>
                    <Form
                      initialValues={{ dateRange }}
                      layout="vertical"
                      onValuesChange={handleValuesChange}
                    >
                      <DateRangeFormComponent scope={scope} />
                    </Form>
                  </div>
                }
                destroyTooltipOnHide
                placement="bottomLeft"
                showArrow={false}
                trigger={["click"]}
              >
                <div className={cn(styles.range, dragStyles.nonDraggable)}>{text}</div>
              </Popover>
            ) : (
              text
            ),
          dateRange: dateRange.range,
          dateSubRange: dateRange.subRange,
          entityList: intl.formatList(
            stageTypes.map((type) => getDealStageTypeDisplayName(intl, type)),
            { type: "conjunction" }
          ),
          stageTypes: stageTypes.length > 0,
        })}
      <Trend
        onMouseEnter={handleTrendMouseEnter}
        onMouseLeave={handleTrendMouseLeave}
        trend={trend}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  stages: Object.values(getFunnelStages(state)).flat(),
});

export default connect(mapStateToProps)(Title);
